import { ErrorBoundary, wrapCreateBrowserRouterV7 } from "@sentry/react";
import { useEffect, useRef, useState } from "react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { Toaster } from "sonner";

import { initAuth, getUser } from "api/cookie_auth";
import { ErrorDisplay } from "components/error-display";
import { ProtectedRoute } from "components/protected-route";
import { paths } from "config/paths";
import { SystemAudioProvider } from "hooks/system-audio-context";
import { AudioSettings } from "screens/audio-settings";
import { Call } from "screens/call";
import { Login } from "screens/login";
import { NotFound } from "screens/not-found";

const sentryCreateBrowserRouter = wrapCreateBrowserRouterV7(createBrowserRouter);

const RootLayout = () => {
  const handleReload = () => {
    window.location.reload();
  };

  const handleBackToLogin = () => {
    window.location.href = "/";
  };

  return (
    <ErrorBoundary
      fallback={
        <ErrorDisplay
          actions={[
            {
              label: "Reload Page",
              onClick: handleReload,
              variant: "primary",
            },
            {
              label: "Back to Login",
              onClick: handleBackToLogin,
              variant: "secondary",
            },
          ]}
          description="An unexpected error occurred. Please try reloading the page or contact support if the problem persists."
          title="Something went wrong"
        />
      }
    >
      <Toaster position="top-center" visibleToasts={1} />
      <SystemAudioProvider>
        <Outlet />
      </SystemAudioProvider>
    </ErrorBoundary>
  );
};

const router = sentryCreateBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      {
        path: paths.login,
        element: <Login />,
      },
      {
        path: paths.audioSettings,
        element: (
          <ProtectedRoute>
            <AudioSettings />
          </ProtectedRoute>
        ),
      },
      {
        path: paths.call,
        element: (
          <ProtectedRoute>
            <Call />
          </ProtectedRoute>
        ),
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
]);

const Router = () => <RouterProvider router={router} />;

export function Entry() {
  const [isInitializing, setIsInitializing] = useState(true);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (hasInitializedRef.current) {
      setIsInitializing(false);
      return;
    }
    hasInitializedRef.current = true;

    let isMounted = true;

    const initialize = async () => {
      try {
        await initAuth();
      } finally {
        if (isMounted) {
          setIsInitializing(false);

          const currentPath = window.location.pathname;
          const isOnLoginPage = currentPath === paths.login || currentPath === "/";
          if (isOnLoginPage) {
            const hasUser = Boolean(getUser());
            const targetPath = hasUser ? paths.audioSettings : paths.login;
            router.navigate(targetPath, { replace: true });
          }
        }
      }
    };

    void initialize();

    return () => {
      isMounted = false;
    };
  }, []);

  if (isInitializing) {
    return (
      <div
        style={{
          display: "grid",
          placeItems: "center",
          minHeight: "100vh",
          color: "#666",
          fontSize: "16px",
        }}
      >
        Checking your session...
      </div>
    );
  }

  return <Router />;
}
